import { Container, Graphics, Sprite, type Texture } from 'pixi.js';
import { AudioBus } from '../../audio/AudioBus';
import type { MorningCopy, TakingsRow } from '../../logic/story/cards';
import { STORY_SOUNDS } from '../../logic/story/sounds';
import { visitorInfo, visitorLine, visitorPrizeLine } from '../../logic/story/visitors';
import { animate, COLOURS, ease, layoutButtonRow, type BoardDialog, type BrassButton } from '../board/index';
import { rowByWords } from './FenHost';
import { body, button, display, fitSprite } from './text';
import { MapPieces, xMarker } from './TreasureMap';

/** Art the story cards draw with, looked up by name. */
export type CardArt = (
  name:
    | 'coin' | 'gem' | 'spin' | 'star' | 'run' | 'takings' | 'morning' | 'bea' | 'fen_talking' | 'fen_cheering' | 'fen_worried'
    | 'visitor_lou' | 'visitor_willa' | 'visitor_carl' | 'treasure_chest' | 'map_piece_1' | 'map_piece_2' | 'map_piece_3',
) => Texture | null;

/** A painted header across the top of a card, with rounded corners, and Fen
 *  (when the story is on) stepping in at its corner. */
function header(art: Texture | null, width: number, fen: Texture | null): Container {
  const c = new Container();
  const h = Math.round(width * 0.42);
  if (art) {
    const s = new Sprite(art);
    const scale = Math.max(width / art.width, h / art.height);
    s.scale.set(scale);
    s.position.set((width - art.width * scale) / 2, (h - art.height * scale) / 2);
    const mask = new Graphics().roundRect(0, 0, width, h, 10).fill(0xffffff);
    s.mask = mask;
    c.addChild(s, mask);
  }
  c.addChild(new Graphics().roundRect(0, 0, width, h, 10).stroke({ color: COLOURS.brass, width: 2 }));
  if (fen) {
    const f = fitSprite(fen, Math.round(h * 0.95));
    f.position.set(width - f.width + 6, h - f.height + 12);
    c.addChild(f);
  }
  return c;
}

/** A row with a coin (or other) picture and a big amount, centred. */
function prizeRow(art: Texture | null, text: string, size: number, width: number, y: number): Container {
  const row = new Container();
  const pic = fitSprite(art, size);
  const amount = display(text, Math.round(size * 0.92));
  amount.position.set(size + 8, Math.round((size - amount.height) / 2));
  row.addChild(pic, amount);
  row.position.set(Math.round((width - row.width) / 2), y);
  row.label = 'prize-row';
  return row;
}

/** The middle of a display object, in canvas pixels. */
function centreOf(node: Container): { x: number; y: number } {
  const b = node.getBounds();
  return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
}

/** Where the coins are, on the canvas (they fly from there) and on the
 *  board (the confetti bursts from there). */
export interface CoinsAt {
  canvas: { x: number; y: number };
  board: { x: number; y: number };
}

function coinsAt(row: Container): CoinsAt {
  return { canvas: centreOf(row), board: { x: row.x + row.width / 2, y: row.y + row.height / 2 } };
}

/**
 * A rival character's gift: Bea (labelled as a character, never a player)
 * cheers the player on and hands over a few coins. THANKS takes them.
 */
export function fillRivalCard(dialog: BoardDialog, o: { art: CardArt; coins: number; runName: string; onThanks: (at: CoinsAt) => void }): void {
  dialog.setTitle('BRAVE BEA');
  const w = dialog.contentWidth;
  const c = dialog.content;
  c.removeChildren().forEach((n) => n.destroy({ children: true }));
  const face = fitSprite(o.art('bea'), 88);
  face.position.set(-6, -4);
  const words = body(`Hello! I'm Bea, one of the Goodtime characters. I'm racing you to the next ${o.runName}, so here are some coins for luck!`, 15, {
    width: w - 90,
    align: 'left',
  });
  words.position.set(88, 0);
  const top = Math.round(Math.max(face.height - 4, words.height) + 10);
  const gift = prizeRow(o.art('coin'), `+${o.coins.toLocaleString('en-GB')}`, 40, w, top);
  const thanks: BrassButton = button('THANKS, BEA', 'gold', () => {
    thanks.setEnabled(false);
    o.onThanks(coinsAt(gift));
  });
  const y = Math.round(gift.y + gift.height + 14);
  layoutButtonRow(null, thanks, 0, y, w);
  c.addChild(face, words, gift, thanks);
  dialog.setContentHeight(y + thanks.buttonHeight + 3);
}

/** Tonight's takings: what the session won, a kind line, and the way out. */
export function fillTakingsCard(
  dialog: BoardDialog,
  o: {
    art: CardArt;
    title: string;
    rows: TakingsRow[];
    line: string;
    fen: 'fen_cheering' | 'fen_talking' | 'fen_worried' | null;
    onStay: () => void;
    onLeave: () => void;
  },
): void {
  dialog.setTitle(o.title);
  const w = dialog.contentWidth;
  const c = dialog.content;
  c.removeChildren().forEach((n) => n.destroy({ children: true }));
  const head = header(o.art('takings'), w, o.fen ? o.art(o.fen) : null);
  c.addChild(head);
  let y = Math.round(head.height + 4);
  for (const r of o.rows) {
    const icon = fitSprite(o.art(r.icon), 26);
    icon.position.set(2, y + 5);
    const label = body(r.label, 16, { align: 'left' });
    label.position.set(36, y + 8);
    const value = display(r.value, 22);
    value.anchor.set(1, 0);
    value.position.set(w - 2, y + 5);
    const rule = new Graphics().rect(0, y + 37, w, 1).fill({ color: COLOURS.brass, alpha: 0.35 });
    c.addChild(icon, label, value, rule);
    y += 38;
  }
  const line = body(o.line, 15, { width: w });
  line.anchor.set(0.5, 0);
  line.position.set(w / 2, y + 8);
  c.addChild(line);
  const stay = button('Keep playing', 'wood', o.onStay, 17);
  const leave = button('SEE YOU SOON', 'gold', o.onLeave, 17);
  const by = Math.round(line.y + line.height + 14);
  layoutButtonRow(stay, leave, 0, by, w);
  c.addChild(stay, leave);
  dialog.setContentHeight(by + leave.buttonHeight + 3);
}

/** The morning post: the day's coins (already credited), the side lights
 *  lit overnight, and the streak. LET'S PLAY flies the coins home. */
export function fillMorningCard(
  dialog: BoardDialog,
  o: { art: CardArt; copy: MorningCopy; coins: number; storyOn: boolean; onPlay: (at: CoinsAt) => void },
): void {
  dialog.setTitle(o.copy.title);
  const w = dialog.contentWidth;
  const c = dialog.content;
  c.removeChildren().forEach((n) => n.destroy({ children: true }));
  const head = header(o.art('morning'), w, o.storyOn ? o.art('fen_talking') : null);
  c.addChild(head);
  const line = body(o.copy.line, 15, { width: w });
  line.anchor.set(0.5, 0);
  line.position.set(w / 2, Math.round(head.height + 10));
  const gift = prizeRow(o.art('coin'), `+${o.coins.toLocaleString('en-GB')}`, 44, w, Math.round(line.y + line.height + 8));
  const streak = body(o.copy.streak, 14, { weight: '500', fill: 0xf3dfb0 });
  streak.anchor.set(0.5, 0);
  streak.position.set(w / 2, Math.round(gift.y + gift.height + 4));
  const play: BrassButton = button("LET'S PLAY", 'gold', () => {
    play.setEnabled(false);
    o.onPlay(coinsAt(gift));
  });
  const by = Math.round(streak.y + streak.height + 14);
  layoutButtonRow(null, play, 0, by, w);
  c.addChild(line, gift, streak, play);
  dialog.setContentHeight(by + play.buttonHeight + 3);
}

/**
 * A visitor drops by with a game: their portrait, who they are (a Goodtime
 * character, never another player), the game, and what it can pay. PLAY
 * (right) takes it up; Not now (left) sends them on.
 */
export function fillVisitorCard(
  dialog: BoardDialog,
  o: { art: CardArt; host: string | undefined; game: string | undefined; coins?: number; floor?: number; onAnswer: (yes: boolean) => void },
): void {
  const v = visitorInfo(o.host);
  dialog.setTitle(v.name);
  const w = dialog.contentWidth;
  const c = dialog.content;
  c.removeChildren().forEach((n) => n.destroy({ children: true }));
  const key = `visitor_${o.host ?? 'lou'}` as 'visitor_lou';
  const face = fitSprite(o.art(key), 92);
  face.position.set(-6, -4);
  face.label = `visitor-${o.host ?? 'lou'}`;
  const words = body(visitorLine(o.host, o.game), 15, { width: w - 94, align: 'left' });
  words.position.set(92, 0);
  const top = Math.round(Math.max(face.height - 4, words.height) + 10);
  const prize = body(visitorPrizeLine(o.coins, o.floor), 14, { width: w, weight: '600', fill: COLOURS.gold });
  prize.anchor.set(0.5, 0);
  prize.position.set(w / 2, top);
  let answered = false;
  const answer = (yes: boolean) => {
    if (answered) return;
    answered = true;
    later.setEnabled(false);
    play.setEnabled(false);
    o.onAnswer(yes);
  };
  const later: BrassButton = button('Not now', 'wood', () => answer(false), 17);
  const play: BrassButton = button('PLAY', 'gold', () => answer(true), 18);
  const by = Math.round(prize.y + prize.height + 14);
  rowByWords(later, play, 0, by, w);
  c.addChild(face, words, prize, later, play);
  dialog.setContentHeight(by + play.buttonHeight + 3);
}

/** The treasure card after it's drawn: put the pieces together, then show
 *  the chest that was dug up. */
export interface TreasureCard {
  /** The torn pieces slide together into the map. */
  assemble(): Promise<void>;
  /** The chest at the X bursts out with `coins`; resolves with where the
   *  coins are, for the confetti and the flight to the wallet. */
  dug(coins: number): Promise<CoinsAt>;
  /** Digging failed (no connection): DIG can be tapped again. */
  retry(): void;
}

/**
 * The treasure map, complete: its three torn pieces come together, the X
 * glows, and DIG digs up the treasure there (the server pays it). One
 * button: the treasure is a gift, there's nothing to turn down.
 */
export function fillTreasureCard(dialog: BoardDialog, o: { art: CardArt; line: string; onDig: () => void }): TreasureCard {
  dialog.setTitle('TREASURE MAP');
  const w = dialog.contentWidth;
  const c = dialog.content;
  c.removeChildren().forEach((n) => n.destroy({ children: true }));
  const map = new MapPieces([o.art('map_piece_1'), o.art('map_piece_2'), o.art('map_piece_3')]);
  map.layout(w);
  map.assemble(0);
  const mapH = MapPieces.heightAt(w);
  const xAt = { x: w * MapPieces.X_AT.x, y: mapH * MapPieces.X_AT.y };
  const glow = xMarker(Math.round(w * 0.34));
  glow.position.set(xAt.x, xAt.y);
  glow.alpha = 0;
  // A warm halo over the X: soft enough that the X still shows through.
  glow.tint = 0xff9a3c;
  const line = body(o.line, 15, { width: w });
  line.anchor.set(0.5, 0);
  line.position.set(w / 2, mapH + 10);
  const dig: BrassButton = button('DIG', 'gold', () => {
    dig.setEnabled(false);
    dig.setLabel('DIGGING');
    o.onDig();
  }, 20);
  const by = Math.round(line.y + line.height + 14);
  layoutButtonRow(null, dig, 0, by, w);
  c.addChild(map, glow, line, dig);
  const contentH = by + dig.buttonHeight + 3;
  dialog.setContentHeight(contentH);

  let pulsing = true;
  const pulse = async () => {
    for (let i = 0; pulsing && i < 40; i++) {
      await animate(700, (t) => {
        if (pulsing) glow.alpha = 0.2 + 0.4 * Math.sin(t * Math.PI);
      });
    }
  };
  return {
    assemble: async () => {
      await animate(700, (t) => map.assemble(ease.outCubic(t)));
      void pulse();
    },
    dug: async (coins) => {
      pulsing = false;
      AudioBus.shared.play(STORY_SOUNDS.treasure, { volume: 0.85 });
      const size = Math.round(Math.min(w * 0.42, mapH * 0.75));
      const chest = fitSprite(o.art('treasure_chest'), size);
      chest.anchor.set(0.5);
      chest.label = 'treasure-chest';
      const cx = Math.min(w - size / 2, Math.max(size / 2, xAt.x));
      const cy = Math.min(mapH - size / 2 + 10, xAt.y);
      chest.position.set(cx, cy);
      const k = chest.scale.x;
      c.addChild(chest);
      dig.visible = false;
      line.visible = false;
      // The prize sits in the middle of where the line and DIG were, so the
      // board keeps its size.
      const gift = prizeRow(o.art('coin'), `+${coins.toLocaleString('en-GB')}`, 44, w, 0);
      gift.y = Math.round(mapH + (contentH - mapH - gift.height) / 2);
      gift.alpha = 0;
      c.addChild(gift);
      await animate(480, (t) => {
        const e = ease.outBack(t);
        chest.scale.set(k * (0.2 + 0.8 * e));
        glow.alpha = 1 - t;
        gift.alpha = t;
      });
      return coinsAt(gift);
    },
    retry: () => {
      dig.setLabel('TRY AGAIN');
      dig.setEnabled(true);
    },
  };
}
